import { type Cluster, ClusterType, type CustomerModelCloudConnectModel } from "@doitintl/cmp-models";
import { type ModelIdRef } from "@doitintl/models-any";

import { type ClusterTableItem } from "./Clusters/Table/types";
import { TimeRange } from "./Dashboard/ClusterDetail/Components/TimeRangeSelector";
import { type ClusterBillingCostComparativeItem, type ClusterBillingCosts, type DashboardCluster } from "./types";

export const getDoitBillingLastDate = () => {
  const doitBillingDaysOffset = 1;

  const today = new Date();
  today.setDate(today.getDate() - doitBillingDaysOffset);
  return today;
};

export const getFormattedBillingCost = (cluster: Cluster | undefined, today?: Date): ClusterBillingCosts => {
  const emptyCost: ClusterBillingCostComparativeItem = {
    cost: 0,
    previousCost: 0,
  };

  const billingCosts: ClusterBillingCosts = {
    [TimeRange.T_1D]: { ...emptyCost },
    [TimeRange.T_3D]: { ...emptyCost },
    [TimeRange.T_7D]: { ...emptyCost },
    [TimeRange.T_14D]: { ...emptyCost },
    [TimeRange.T_30D]: { ...emptyCost },
  };

  if (!cluster?.billingCosts) {
    return billingCosts;
  }

  const sortedBilling = Object.entries(cluster.billingCosts).sort(
    ([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime()
  );

  const latestDate = today || getDoitBillingLastDate();

  sortedBilling.forEach(([date, cost]) => {
    const billingDate = new Date(date);
    const diffInDays = Math.floor((latestDate.getTime() - billingDate.getTime()) / (1000 * 60 * 60 * 24));

    Object.keys(billingCosts).forEach((timeRange) => {
      const range = parseInt(timeRange, 10);

      if (diffInDays < range) {
        billingCosts[timeRange as keyof ClusterBillingCosts].cost += +cost;
      } else if (diffInDays < range * 2) {
        billingCosts[timeRange as keyof ClusterBillingCosts].previousCost += +cost;
      }
    });
  });

  return billingCosts;
};

export const getTopTenClustersByCost = (clusters: DashboardCluster[], timeRange: TimeRange, today?: Date) => {
  const latestDate = today || getDoitBillingLastDate();
  const rangeDays = parseInt(timeRange, 10);

  const formatBillingCosts = (cluster: DashboardCluster) => {
    const billingCosts = cluster.doitCluster?.billingCosts ?? {};
    const costs = Object.entries(billingCosts)
      .map(([date, cost]) => ({
        time: date,
        cost: +cost,
      }))
      .filter(({ time }) => {
        const diffInDays = Math.floor((latestDate.getTime() - new Date(time).getTime()) / (1000 * 60 * 60 * 24));
        return diffInDays < rangeDays;
      })
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    return {
      clusterName: cluster.name,
      costs,
    };
  };

  return clusters
    .map((cluster) => ({
      ...cluster,
      totalCost: cluster.doitBillingCosts?.[timeRange]?.cost ?? 0,
    }))
    .sort((a, b) => b.totalCost - a.totalCost)
    .slice(0, 10)
    .map(formatBillingCosts);
};

export const findMatchingCloudConnectDoc = (
  cluster: Cluster,
  cloudConnectDocs: ModelIdRef<CustomerModelCloudConnectModel>[]
) => {
  // 1. Try to find cloud connect by the cluster's ref
  const matchingCloudConnectDoc = cloudConnectDocs?.find((doc) => doc.id === cluster.cloudConnectRef?.id);

  if (matchingCloudConnectDoc) {
    return matchingCloudConnectDoc;
  }

  // 2. If we can't get any match, try to find the cloud connect by the projectId
  const matchingDocByProjectId = cloudConnectDocs?.find((doc) => {
    if (cluster.type === ClusterType.EKS) {
      return doc.cloudPlatform === "amazon-web-services" && doc.accountId === cluster.projectId;
    }

    if (cluster.type === ClusterType.GKE) {
      return doc.cloudPlatform === "google-cloud" && doc.projectId === cluster.projectId;
    }

    return false;
  });

  if (matchingDocByProjectId) {
    return matchingDocByProjectId;
  }

  return undefined;
};

export const clusterHasPsData = (cluster: DashboardCluster, hoursToWait = 24) => {
  const agentInstallationTime = cluster.doitCluster?.perfectScale?.AgentInstallationTime?.toDate();
  return agentInstallationTime && agentInstallationTime.getTime() < Date.now() - hoursToWait * 60 * 60 * 1000;
};

export const clusterHas30dPerfectScaleData = (cluster: Cluster | undefined) =>
  Object.keys(cluster?.perfectScale30DaysInfo ?? {}).length > 0;

export const clusterHasAgent = (cluster: Cluster) => !!cluster?.perfectScale?.PsClusterID;

export const getEmptyDashboardCluster = (cluster: ClusterTableItem): DashboardCluster => ({
  meta: { dataStatus: "", requestedMinutes: 0, successfulMinutes: 0 },
  name: cluster.name,
  cloud: cluster.type,
  cost: 0,
  cpuRequests: 0,
  memRequests: 0,
  potentialSavings: 0,
  perfectscaleCost: 0,
  doitBillingCosts: {
    "1d": { cost: 0, previousCost: 0 },
    "3d": { cost: 0, previousCost: 0 },
    "7d": { cost: 0, previousCost: 0 },
    "14d": { cost: 0, previousCost: 0 },
    "30d": { cost: 0, previousCost: 0 },
  },
  doitCluster: cluster,
  workloads: 0,
  hasDataForSelectedTimeRange: false,
});
